/**
 * Timezone utilities for Admin Dashboard
 * 
 * All times are stored in UTC on the backend but displayed in US East Coast time
 * (America/New_York) which automatically handles EST/EDT daylight savings transitions.
 */

/** The target timezone for display - US East Coast */
export const DISPLAY_TIMEZONE = "America/New_York";

/** Short label for the timezone indicator */
export const TIMEZONE_LABEL = "ET"; // Eastern Time (covers both EST and EDT)

/**
 * Format a timestamp (number or ISO string) to East Coast time
 * Returns full date and time (e.g., "1/29/2026, 2:15:17 AM")
 */
export function formatDateTime(timestamp: number | string | null | undefined): string {
  if (!timestamp) return "Never";
  
  const date = typeof timestamp === "number" ? new Date(timestamp) : new Date(timestamp);
  
  if (isNaN(date.getTime())) return "Invalid date";
  
  return date.toLocaleString("en-US", {
    timeZone: DISPLAY_TIMEZONE,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

/**
 * Format a timestamp to East Coast time - time only
 * Returns just the time (e.g., "2:15:17 AM")
 */
export function formatTime(timestamp: number | string | null | undefined): string {
  if (!timestamp) return "Never";
  
  const date = typeof timestamp === "number" ? new Date(timestamp) : new Date(timestamp);
  
  if (isNaN(date.getTime())) return "Invalid time";
  
  return date.toLocaleString("en-US", {
    timeZone: DISPLAY_TIMEZONE,
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

/**
 * Format a timestamp to East Coast time - short time (no seconds)
 * Returns short time (e.g., "2:15 AM")
 */
export function formatTimeShort(timestamp: number | string | null | undefined): string {
  if (!timestamp) return "Never";
  
  const date = typeof timestamp === "number" ? new Date(timestamp) : new Date(timestamp);
  
  if (isNaN(date.getTime())) return "Invalid time";
  
  return date.toLocaleString("en-US", {
    timeZone: DISPLAY_TIMEZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Format a timestamp to East Coast time - date only
 * Returns just the date (e.g., "1/29/2026")
 */
export function formatDate(timestamp: number | string | null | undefined): string {
  if (!timestamp) return "Never";
  
  const date = typeof timestamp === "number" ? new Date(timestamp) : new Date(timestamp);
  
  if (isNaN(date.getTime())) return "Invalid date";
  
  return date.toLocaleString("en-US", {
    timeZone: DISPLAY_TIMEZONE,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

/**
 * Format a timestamp to East Coast time - short date
 * Returns abbreviated date (e.g., "Jan 29")
 */
export function formatDateShort(timestamp: number | string | null | undefined): string {
  if (!timestamp) return "—";
  
  const date = typeof timestamp === "number" ? new Date(timestamp) : new Date(timestamp);
  
  if (isNaN(date.getTime())) return "Invalid";
  
  return date.toLocaleString("en-US", {
    timeZone: DISPLAY_TIMEZONE,
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a Date object to East Coast time - used for "Last updated: X" displays
 */
export function formatLastUpdated(date: Date | null): string {
  if (!date) return "";
  
  return date.toLocaleString("en-US", {
    timeZone: DISPLAY_TIMEZONE,
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

/**
 * Get current time in East Coast timezone as formatted string
 */
export function getCurrentTimeET(): string {
  return new Date().toLocaleString("en-US", {
    timeZone: DISPLAY_TIMEZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Check if we're currently in Eastern Daylight Time (EDT) vs Eastern Standard Time (EST)
 * Useful for displaying "EST" vs "EDT" if needed
 */
export function isEDT(): boolean {
  const now = new Date();
  const currentOffset = now.toLocaleString("en-US", { timeZone: DISPLAY_TIMEZONE, timeZoneName: "short" });
  
  // If current time string contains "EDT", we're in daylight time
  return currentOffset.includes("EDT");
}

/**
 * Get the current timezone abbreviation (EST or EDT)
 */
export function getTimezoneAbbreviation(): string {
  return isEDT() ? "EDT" : "EST";
}

/**
 * Format a relative time from now (e.g., "5m 30s")
 * Works with future timestamps for countdown displays
 */
export function formatRelativeTime(timestamp: number | null): string {
  if (!timestamp) return "—";
  
  const now = Date.now();
  const diff = timestamp - now;
  
  if (diff <= 0) return "Now";
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

/**
 * Format a duration in minutes to human-readable string
 */
export function formatDuration(minutes: number | null): string {
  if (!minutes || minutes === 0) return "—";
  
  if (minutes < 60) return `${minutes}m`;
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}
